import globals from "@lvt/globals";
import { SerializedError, createAsyncThunk } from "@reduxjs/toolkit";
import Ajv from "ajv";
import axios from "axios";
import applyCaseMiddleware from "axios-case-converter";


export interface UserPermissions {
    archives: boolean;
    cameras: boolean;
    streamDeactivated: boolean;
    d3cc: boolean;
    selfService: boolean;
    liveUnits: boolean;
    clients: boolean;
    locations: boolean;
    users: boolean;
    quickConfig: boolean;
    reportViewAll: boolean;
}

export interface UserInfo {
    id: number;
    lvtc: string;
    lvtu: string;
    lvtr: string;
    username: string;
    name: string;
    permissions: UserPermissions;
    timeZone: string | null;
    videoPreference: string;
}

const fetchUserInfoSchema = {
    type: "object",
    required: ["status", "statusMessage", "data"],
    additionalProperties: false,
    properties: {
        status: { const: 200 },
        statusMessage: { type: "string" },
        data: {
            type: "object",
            required: ["id", "lvtc", "lvtu", "lvtr", "name", "permissions", "username"],
            properties: {
                id: { type: "integer" },
                lvtc: { type: "string" },
                lvtu: { type: "string" },
                lvtr: { type: "string" },
                name: { type: "string" },
                username: { type: "string" },
                videoPreference: { type: "string" },
                permissions: {
                    type: "object",
                    required: ["d3cc", "users"],
                    additionalProperties: true,
                    properties: {
                        archives: { type: "boolean" },
                        cameras: { type: "boolean" },
                        streamDeactivated: { type: "boolean" },
                        d3cc: { type: "boolean" },
                        selfService: { type: "boolean" },
                        liveUnits: { type: "boolean" },
                        clients: { type: "boolean" },
                        locations: { type: "boolean" },
                        users: { type: "boolean" },
                        quickConfig: { type: "boolean" },
                        reportViewAll: { type: "boolean" },
                    },
                },
            },
        },
    },
};

const fetchUserInfoValidator = new Ajv().compile(fetchUserInfoSchema);

export const fetchUserInfo = createAsyncThunk("fetchUserInfo", async () => {
    const client = applyCaseMiddleware(
        axios.create({
            baseURL: globals.userdashboard.proxyUrl,
        }),
    );

    const response = await client.get("/api/vms/user_info", {
        withCredentials: true,
    });

    if (fetchUserInfoValidator(response.data)) {
        return response.data.data as UserInfo;
    } else {
        const error: SerializedError = {
            name: "validation",
            message: JSON.stringify(fetchUserInfoValidator.errors),
        };

        throw error;
    }
});

export interface UserIdentity {
    clientId: number;
    userId: number;
    userToken: string;
}

export const fetchUserIdentity = async (url: string): Promise<UserIdentity> => {
    const resp = await axios.get(url, {
        withCredentials: true,
    });

    return resp.data.data;
};
